/**
 * ActivationPaywall (self-contained JSX page)
 * -------------------------------------------
 * UX goals:
 *  - FinTorro look (dark, orange highlight, green CTA).
 *  - Centered “card” layout with clearly separated sections (like Duolingo).
 *  - Two plans by default (Monthly, Yearly). No free trial shown initially.
 *  - If the user taps Continue with no plan 3 times, reveal the 2-week trial card.
 *  - Block checkout when nothing is selected (inline error).
 *  - Send users to Stripe Checkout (via your backend session endpoint).
 *
 * How Stripe is called:
 *  - We POST to `/api/stripe/create-checkout-session` with { plan: 'monthly'|'yearly'|'trial' }.
 *  - The endpoint must return `{ url: 'https://checkout.stripe.com/...' }`,
 *    then we `window.location.href = url`.
 *
 * Branding tokens:
 *  - Orange (accent):  #FF7C24
 *  - Green (cta):      #2ECC71
 *  - Card bg:          #0F141B
 *  - Ink:              #DDE2EA
 *
 * NOTE: Keep this file standalone as requested—no imports for external css.
 */

import React, { useMemo, useState } from "react";

export default function ActivationPaywall() {
  // what the user picked: 'monthly' | 'yearly' | 'trial' | null
  const [selected, setSelected] = useState(null);
  // count how many times the user pressed Continue without selection
  const [nudges, setNudges] = useState(0);
  // free trial is revealed only after 3rd nudge
  const showTrial = nudges >= 3;
  // inline error message
  const [error, setError] = useState("");

  // pricing copy (feel free to wire this to CMS or locale)
  const plans = useMemo(
    () => [
      {
        id: "monthly",
        title: "Monthly",
        priceMain: "£9.99",
        priceSuffix: "/mo",
        badges: ["Flexible monthly billing"],
        ribbon: "",
      },
      {
        id: "yearly",
        title: "Yearly",
        priceMain: "£119.99",
        priceSuffix: "/yr",
        badges: ["Best value", "Billed annually"],
        ribbon: "Save 17%",
      },
      // Revealed on 3rd nudge (not visible initially)
      ...(showTrial
        ? [
            {
              id: "trial",
              title: "2-Week Free Trial",
              priceMain: "£0",
              priceSuffix: "for 14 days",
              badges: ["Full access for 14 days"],
              ribbon: "Most Popular",
              emphasis: true,
            },
          ]
        : []),
    ],
    [showTrial]
  );

  async function startCheckout(planId) {
    try {
      // Clear stale error
      setError("");

      // Server should create a Checkout Session and return { url }
      const res = await fetch("/api/stripe/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: planId, // 'monthly' | 'yearly' | 'trial'
          // You can add attribution context here if you’d like
          source: "activation-paywall",
        }),
      });

      if (!res.ok) {
        throw new Error(`Checkout failed (${res.status})`);
      }
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url; // Redirect to Stripe Checkout
      } else {
        throw new Error("Missing Checkout URL from server");
      }
    } catch (err) {
      console.error(err);
      setError("We couldn't start checkout. Please try again.");
    }
  }

  function onContinue() {
    // If nothing picked, nudge + possibly reveal trial
    if (!selected) {
      const next = nudges + 1;
      setNudges(next);
      setError("Please choose a plan to continue.");
      return;
    }
    // Otherwise: head to Stripe
    startCheckout(selected);
  }

  return (
    <div style={styles.page}>
      {/* Page-wide styles (fonts, focus ring, subtle glow) */}
      <style>{cssGlobal}</style>

      {/* Header (brand) */}
      <header style={styles.header}>
        <div style={styles.brandRow}>
          <div style={styles.logoBadge}>🐂</div>
          <div style={styles.brand}>FinTorro</div>
        </div>

        <div style={styles.progressHint}>You’re on a roll 🚀</div>
        <div style={styles.subprogress}>
          Unlock guided lessons, daily drills, and the full FinTorro toolkit.
        </div>
      </header>

      {/* Centered content card */}
      <main style={styles.main}>
        <div style={styles.card}>
          {/* Title block */}
          <div style={styles.titleBlock}>
            <h1 style={styles.h1}>Pick your plan</h1>
            <p style={styles.subtitle}>
              Choose a plan to keep learning without limits. Cancel anytime.
            </p>
          </div>

          {/* Plan tiles */}
          <div style={styles.planGrid}>
            {plans.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  setSelected(p.id);
                  setError("");
                }}
                aria-pressed={selected === p.id}
                style={{
                  ...styles.planCard,
                  ...(selected === p.id ? styles.planCardSelected : {}),
                  ...(p.emphasis ? styles.planCardEmphasis : {}),
                }}
              >
                {!!p.ribbon && (
                  <div style={styles.ribbon}>
                    <span>{p.ribbon}</span>
                  </div>
                )}
                <div style={styles.planTitle}>{p.title}</div>
                <div style={styles.priceRow}>
                  <span style={styles.priceMain}>{p.priceMain}</span>
                  <span style={styles.priceSuffix}>{p.priceSuffix}</span>
                </div>
                <ul style={styles.badgeList}>
                  {p.badges.map((b) => (
                    <li key={b} style={styles.badgeItem}>
                      {b}
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>

          {/* Value props */}
          <div style={styles.featuresGrid}>
            <div style={styles.featureChip}>
              <span style={styles.spark}>⚡</span> Unlimited Lessons & Practice
            </div>
            <div style={styles.featureChip}>
              <span style={styles.spark}>⚡</span> AI-guided charts & feedback
            </div>
            <div style={styles.featureChip}>
              <span style={styles.spark}>⚡</span> 10,000+ interactive drills
            </div>
            <div style={styles.featureChip}>
              <span style={styles.spark}>⚡</span> Crypto, Forex, Stocks & More
            </div>
            <div style={styles.featureChip}>
              <span style={styles.spark}>⚡</span> Challenges, rewards & streaks
            </div>
          </div>

          {/* Guarantees */}
          <div style={styles.guarantees}>
            <span style={styles.guaranteePill}>7-day money-back guarantee</span>
            <span style={styles.dot}>•</span>
            <span style={styles.guaranteePill}>
              Secure checkout powered by Stripe
            </span>
          </div>

          {/* Error (no special-offer banners per request) */}
          {error && <div style={styles.error}>{error}</div>}
        </div>
      </main>

      {/* Sticky bottom bar with the single Continue CTA (Duolingo-style) */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <button onClick={onContinue} style={styles.cta}>
            Continue →
          </button>

          <div style={styles.legalRow}>
            <span>By continuing you agree to our</span>{" "}
            <a href="/terms" style={styles.link}>
              Terms
            </a>{" "}
            and{" "}
            <a href="/privacy" style={styles.link}>
              Privacy Policy
            </a>
            .
          </div>

          {/* Subtle hint (appears ONLY once trial is revealed) */}
          {showTrial && (
            <div style={styles.trialHint}>
              2-Week Free Trial unlocked — select it above to try FinTorro Pro.
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

/* -------------------------- Styles (JS-in-CSS) -------------------------- */

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0B0D11",
    color: "#DDE2EA",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    fontFamily: "'Nunito Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },

  header: {
    padding: "20px 20px 8px",
    textAlign: "center",
  },
  brandRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  logoBadge: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: "linear-gradient(180deg, #1B2430, #121820)",
    display: "grid",
    placeItems: "center",
    fontSize: 16,
    boxShadow: "0 6px 18px rgba(0,0,0,.35)",
  },
  brand: { fontWeight: 900, letterSpacing: .2, color: "#DDE2EA" },
  progressHint: {
    fontWeight: 800,
    color: "#FF7C24",
    fontSize: 18,
    marginTop: 10,
  },
  subprogress: {
    marginTop: 6,
    display: "inline-block",
    fontSize: 12.5,
    opacity: 0.75,
    padding: "6px 10px",
    borderRadius: 10,
    background: "rgba(255,124,36,.08)",
    border: "1px solid rgba(255,124,36,.18)",
  },

  main: {
    display: "grid",
    placeItems: "center",
    padding: "16px",
  },
  card: {
    width: "min(980px, 100%)",
    background: "#0F141B",
    border: "1px solid #1E2630",
    borderRadius: 20,
    padding: 24,
    boxShadow: "0 10px 40px rgba(0,0,0,.35)",
  },

  titleBlock: { textAlign: "center", marginBottom: 18 },
  h1: {
    margin: 0,
    color: "#FF7C24",
    fontWeight: 900,
    fontSize: 26,
    letterSpacing: .2,
  },
  subtitle: { margin: "8px 0 0", opacity: 0.8, fontSize: 14 },

  planGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
    marginTop: 14,
  },
  planCard: {
    position: "relative",
    textAlign: "left",
    background: "linear-gradient(180deg, #101720, #0D1218)",
    border: "1px solid #1E2630",
    borderRadius: 14,
    padding: 14,
    cursor: "pointer",
    transition: "transform .15s ease, border-color .15s ease, box-shadow .2s ease",
    outline: "none",
  },
  planCardSelected: {
    border: "1px solid #2ECC71",
    boxShadow: "0 0 0 6px rgba(46,204,113,.12)",
    transform: "translateY(-1px)",
  },
  planCardEmphasis: {
    boxShadow: "0 0 0 1px rgba(255,124,36,.45), 0 10px 28px rgba(255,124,36,.12)",
  },
  ribbon: {
    position: "absolute",
    top: -10,
    right: 12,
    background: "#FF7C24",
    color: "#0A0F14",
    borderRadius: 8,
    padding: "3px 8px",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: .2,
    transform: "rotate(0.25deg)",
    boxShadow: "0 6px 16px rgba(255,124,36,.25)",
  },
  planTitle: { fontWeight: 900, fontSize: 16, marginBottom: 8 },
  priceRow: { display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 },
  priceMain: { fontSize: 26, fontWeight: 900, color: "#DDE2EA" },
  priceSuffix: { fontSize: 12.5, opacity: 0.75 },
  badgeList: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 },
  badgeItem: {
    fontSize: 12.5,
    opacity: 0.9,
    padding: "6px 8px",
    borderRadius: 8,
    border: "1px solid #1E2630",
    background: "rgba(255,255,255,.02)",
  },

  featuresGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 10,
    marginTop: 18,
  },
  featureChip: {
    border: "1px solid #1E2630",
    background: "linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,.015))",
    padding: "10px 12px",
    borderRadius: 12,
    fontSize: 13.5,
    fontWeight: 700,
  },
  spark: { marginRight: 8 },

  guarantees: {
    marginTop: 14,
    fontSize: 12.5,
    opacity: 0.85,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  guaranteePill: {
    border: "1px solid #243040",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(221,226,234,.05)",
  },
  dot: { opacity: 0.5 },

  error: {
    marginTop: 12,
    background: "rgba(255, 80, 80, .12)",
    border: "1px solid rgba(255, 80, 80, .3)",
    color: "#ffd3d3",
    padding: "10px 12px",
    borderRadius: 10,
    fontWeight: 700,
    textAlign: "center",
  },

  footer: {
    position: "sticky",
    bottom: 0,
    background:
      "linear-gradient(180deg, rgba(11,13,17,0), rgba(11,13,17,.6) 24%, rgba(11,13,17,.85) 40%, rgba(11,13,17,1) 70%)",
    backdropFilter: "saturate(120%) blur(4px)",
    borderTop: "1px solid #141b23",
  },
  footerInner: {
    width: "min(980px, 100%)",
    margin: "0 auto",
    padding: "14px 16px 18px",
    display: "grid",
    gap: 10,
    placeItems: "center",
  },
  cta: {
    appearance: "none",
    border: "1px solid #2ECC71",
    background:
      "linear-gradient(180deg, rgba(46,204,113,.22), rgba(46,204,113,.16))",
    color: "#DDFEE6",
    fontWeight: 900,
    fontSize: 16,
    padding: "12px 22px",
    borderRadius: 14,
    cursor: "pointer",
    boxShadow:
      "0 12px 40px rgba(46,204,113,.28), inset 0 -2px 0 rgba(0,0,0,.25)",
  },
  legalRow: {
    fontSize: 12,
    opacity: 0.75,
  },
  link: { color: "#9ED7B8", textDecoration: "none" },
  trialHint: {
    fontSize: 12.5,
    color: "#FFCE8C",
    background: "rgba(255,124,36,.10)",
    border: "1px solid rgba(255,124,36,.25)",
    padding: "8px 10px",
    borderRadius: 10,
    fontWeight: 800,
  },
};

const cssGlobal = `
  :root { --ft-orange:#FF7C24; --ft-green:#2ECC71; }
  * { box-sizing: border-box; }
  button:focus-visible {
    outline: 2px solid var(--ft-orange);
    outline-offset: 2px;
  }
`;
